// StudioExport — the Export button at the Studio's top right and the list of
// the project's issues it opens (pen "Q17 · The creation flow", scene M;
// PS-54: the export check is visible all the time). Pure: it shows the
// server's answer (`exportCheck.ts`) and reports a row's thing up through
// `onOpen`; whether the list is open is view state.
//
// The button says "{n} issues" with a warning dot, "Ready", or "Nothing
// yet"; only issues open the list (the Export box itself is #244). A row
// says what and where; a click closes the list and opens that thing. A row
// with nothing to open in the Studio yet (a goal: the coverage lens is not
// built) shows without a chevron. The list holds at most the server's
// ceiling of rows and counts the rest.

export { issueKey, StudioExport, useIssueText };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type {
    IssuePlace,
    ProjectIssue,
    ProjectIssues,
} from "../../app/ports/project.js";
import { Button } from "../Button/Button.js";
import { Dialog } from "../Dialog/Dialog.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import {
    type ExportButton,
    exportButtonOf,
    issueTarget,
} from "./exportCheck.js";
import "./StudioExport.css";

type StudioExportProps = {
    readonly projectTitle: string;
    // The server's last answer; undefined: none yet.
    readonly issues: ProjectIssues | undefined;
    readonly online: boolean;
    // Open this thing (a resource id) where the Studio opens things.
    readonly onOpen: (id: string) => void;
};

const StudioExport = ({
    projectTitle,
    issues,
    online,
    onOpen,
}: StudioExportProps): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState(false);
    const button = exportButtonOf(issues, online);
    const rows = issues?.type === "project_has_issues" ? issues : undefined;
    return (
        <>
            <button
                type="button"
                className="bilda-studio-export"
                disabled={rows === undefined}
                aria-haspopup="dialog"
                onClick={() => setOpen(true)}
            >
                <Icon name="upload" />
                <span className="bilda-studio-export__label">{t`Export`}</span>
                <ButtonStatus button={button} />
            </button>
            {rows === undefined ? null : (
                <Dialog
                    open={open}
                    onOpenChange={setOpen}
                    className="bilda-studio-export__dialog"
                    title={t`${plural(rows.total, {
                        one: `# issue in “${projectTitle}”`,
                        other: `# issues in “${projectTitle}”`,
                    })}`}
                    description={t`Click one to go straight to it.`}
                    footer={
                        <Button
                            variant="secondary"
                            onClick={() => setOpen(false)}
                        >
                            {t`Close`}
                        </Button>
                    }
                >
                    <ul className="bilda-studio-export__list">
                        {rows.issues.map((issue, i) => (
                            <li key={issueKey(issue, i)}>
                                <IssueRow
                                    issue={issue}
                                    onOpen={(id) => {
                                        setOpen(false);
                                        onOpen(id);
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                    {rows.total > rows.issues.length ? (
                        <p className="bilda-studio-export__more">
                            {t`and ${rows.total - rows.issues.length} more`}
                        </p>
                    ) : null}
                </Dialog>
            )}
        </>
    );
};

const ButtonStatus = ({ button }: { readonly button: ExportButton }) => {
    const { t } = useLingui();
    switch (button.type) {
        case "checking":
            return null;
        case "offline":
            return (
                <span className="bilda-studio-export__status">
                    {t`Needs a connection`}
                </span>
            );
        case "nothing_yet":
            return (
                <span className="bilda-studio-export__status">
                    {t`Nothing yet`}
                </span>
            );
        case "ready":
            return (
                <span className="bilda-studio-export__status">{t`Ready`}</span>
            );
        case "issues":
            return (
                <span className="bilda-studio-export__status">
                    <span
                        className="bilda-studio-export__dot"
                        aria-hidden="true"
                    />
                    {t`${plural(button.count, {
                        one: "# issue",
                        other: "# issues",
                    })}`}
                </span>
            );
        default:
            return exhaustSwitchCase(button);
    }
};

// Stable per row: its kind and what it names (the index only breaks a tie
// between unknown rows).
const issueKey = (issue: ProjectIssue, i: number): string => {
    switch (issue.type) {
        case "issue_objectives_not_covered":
            return `${issue.type}:${issue.goal.id}`;
        case "unknown":
            return `${issue.type}:${i}`;
        default:
            return `${issue.type}:${issueTarget(issue) ?? i}`;
    }
};

const within = (thing: IssuePlace, container?: IssuePlace): string =>
    container === undefined
        ? thing.title
        : `${container.title} › ${thing.title}`;

type RowText = {
    readonly icon: IconName;
    readonly what: string;
    readonly where: string;
};

// What a row says about an issue: its icon, what it is, and where.
const useIssueText = (issue: ProjectIssue): RowText => {
    const { t } = useLingui();
    switch (issue.type) {
        case "issue_parts_planned":
            return {
                icon: "warning",
                what: t`${plural(issue.parts.length, {
                    one: "# planned part with nothing in it",
                    other: "# planned parts with nothing in them",
                })}`,
                where: issue.container.title,
            };
        case "issue_parts_empty":
            return {
                icon: "warning",
                what: t`${plural(issue.parts.length, {
                    one: "# empty part",
                    other: "# empty parts",
                })}`,
                where: issue.container.title,
            };
        case "issue_parts_removed":
            return {
                icon: "warning",
                what: t`${plural(issue.parts.length, {
                    one: "# removed part still listed",
                    other: "# removed parts still listed",
                })}`,
                where: issue.container.title,
            };
        case "issue_objectives_not_covered": {
            const goal = issue.goal.title;
            return {
                icon: "warning",
                what: t`${plural(issue.objectives.length, {
                    one: "# learning goal no material covers",
                    other: "# learning goals no material covers",
                })}`,
                where: t`Goal “${goal}”`,
            };
        }
        case "issue_draft_unreviewed":
            return {
                icon: "sparkle",
                what: t`AI draft not reviewed`,
                where: within(issue.part, issue.container),
            };
        case "issue_proposals_open":
            return {
                icon: "sparkle",
                what: t`${plural(issue.count, {
                    one: "# proposal waiting",
                    other: "# proposals waiting",
                })}`,
                where: within(issue.material, issue.container),
            };
        case "unknown":
            return {
                icon: "info",
                what: t`Something else to check`,
                where: "",
            };
        default:
            return exhaustSwitchCase(issue);
    }
};

const IssueRow = ({
    issue,
    onOpen,
}: {
    readonly issue: ProjectIssue;
    readonly onOpen: (id: string) => void;
}) => {
    const text = useIssueText(issue);
    const target = issueTarget(issue);
    const body = (
        <>
            <span
                className="bilda-studio-export__icon"
                data-icon={text.icon}
                aria-hidden="true"
            >
                <Icon name={text.icon} />
            </span>
            <span className="bilda-studio-export__text">
                <span className="bilda-studio-export__what">{text.what}</span>
                {text.where === "" ? null : (
                    <span
                        className="bilda-studio-export__where"
                        title={text.where}
                    >
                        {text.where}
                    </span>
                )}
            </span>
        </>
    );
    return target === undefined ? (
        <div className="bilda-studio-export__row">{body}</div>
    ) : (
        <button
            type="button"
            className="bilda-studio-export__row"
            onClick={() => onOpen(target)}
        >
            {body}
            <Icon name="chevron-right" />
        </button>
    );
};
